import { css } from '@emotion/react'

import type { Theme } from './theme'

/**
 * Global styles for the application
 * These styles are applied to the entire application via Emotion's Global component.
 */
export const globalStyles = (theme: Theme) => css`
  /* CSS Reset and Base Styles */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: ${theme.eclipseTier2Typography.bodyMd.fontFamily};
    font-size: ${theme.eclipseTier2Typography.bodyMd.fontSize};
    line-height: ${theme.eclipseTier2Typography.bodyMd.lineHeight};
    color: ${theme.eclipseTier2Color.text.default};
    background-color: ${theme.eclipseTier2Color.surface.base};
  }

  /* Headings */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
    font-family: ${theme.eclipseTier2Typography.headingMd.fontFamily};
    font-weight: ${theme.eclipseTier1.typography.fontWeight.medium};
    line-height: 1.2;
  }

  h1 {
    font-size: ${theme.eclipseTier2Typography.headingLg.fontSize};
  }

  h2,
  h3 {
    font-size: ${theme.eclipseTier2Typography.headingMd.fontSize};
  }

  /* Links */
  a {
    color: ${theme.eclipseTier2Color.text.brand.default};
    text-decoration: none;
    transition: color ${theme.variables.animationDuration.normal};

    &:hover {
      color: ${theme.eclipseTier2Color.text.brand.hovered};
    }

    &:active {
      color: ${theme.eclipseTier2Color.text.brand.pressed};
    }
  }

  /* Focus styles for accessibility */
  *:focus-visible {
    outline: ${theme.eclipseTier2Border.width.focused} solid ${theme.eclipseTier2Color.border.focus};
    outline-offset: 2px;
  }

  /* Remove default button styles */
  button {
    font-family: inherit;
    cursor: pointer;
  }

  /* Form elements */
  input,
  textarea,
  select {
    font-family: inherit;
    font-size: inherit;
  }

  /* Remove default list styles */
  ul,
  ol {
    margin: 0;
    padding: 0;
  }

  /* Paragraph spacing */
  p {
    margin: 0;
  }

  /* Image defaults */
  img {
    max-width: 100%;
    height: auto;
  }

  /* Scrollbar styling (optional, controlled by SuiProvider) */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${theme.eclipseTier2Color.background.neutral.subtlest.default};
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.eclipseTier2Color.border.subtle};
    border-radius: ${theme.eclipseTier1.radius['8px']};

    &:hover {
      background: ${theme.eclipseTier2Color.border.bold};
    }
  }
`
